import React, { useEffect, useState } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import { Table, Space, PageHeader, Button } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { reverse } from "named-urls";
import { sliceKey, reducer, actions } from "./slice";
import { companyListingSaga } from "./saga";
import * as selectors from "./selectors";
import { addressToString } from "../../../../utils/formatter";
import { useAuth } from "../../../../contexts/auth";
import routes from "../../../../../routes";

const STORAGE_KEY = 'companyListingFilters';

const CompanyListing = () => {
  const auth = useAuth();
  const user = auth.loggedInUser;
  const userRole = user?.role;
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: companyListingSaga });
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  // Initialize state from sessionStorage
  const getInitialState = () => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to parse saved filters:', e);
    }
    return { page: 1, companyName: null };
  };

  const initialState = getInitialState();
  const [currentPage, setCurrentPage] = useState(initialState.page);
  const [companyNameFilter, setCompanyNameFilter] = useState(initialState.companyName);

  const companies = useSelector(selectors.selectCompanies) || [];
  const loading = useSelector(selectors.selectLoading);
  const totalCompanyCount = useSelector(selectors.selectCompanyCount);
  const totalCompanies = useSelector(selectors.selectTotalCompanies);

  // Save state to sessionStorage whenever it changes
  useEffect(() => {
    const state = { page: currentPage, companyName: companyNameFilter };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.error('Failed to save filters:', e);
    }
  }, [currentPage, companyNameFilter]);

  // Fetch companies when filters change
  useEffect(() => {
    let query = `page=${currentPage}&limit=10`;
    if (companyNameFilter) query += `&companyName=${companyNameFilter}`;
    dispatch(actions.fetchCompanies({ query }));
  }, [dispatch, currentPage, companyNameFilter]);

  // Fetch total companies on mount
  useEffect(() => {
    dispatch(actions.fetchTotalCompanies());
  }, [dispatch]);

  const companyData = companies.map((company) => ({
    id: company._id,
    key: company._id,
    company: company.companyName,
    businessType: company.businessType?.label,
    registeredAddress: addressToString(company.registeredAddress),
  }));

  const getCompanyFilters = () => {
    if (!totalCompanies) return [];
    return totalCompanies
      .map((entity) => ({
        text: entity.companyName,
        value: entity.companyName,
      }))
      .sort((a, b) => a.text.localeCompare(b.text));
  };

  const handleTablePagination = (pagination, filters, sorter) => {
    setCurrentPage(pagination.current);
    setCompanyNameFilter(filters.company?.[0] || null);
  };

  const columns = [
    {
      title: "Company Name",
      dataIndex: "company",
      key: "company",
      filters: getCompanyFilters(),
      filterSearch: true,
      filteredValue: companyNameFilter ? [companyNameFilter] : null,
    },
    {
      title: "Business Type",
      dataIndex: "businessType",
      key: "businessType",
      filterSearch: true,
    },
    {
      title: "Registered Address",
      dataIndex: "registeredAddress",
      key: "registeredAddress",
    },
    ...(userRole === "support_team"
      ? []
      : [
          {
            title: "Actions",
            key: "actions",
            render: (value, record) => (
              <Space size="middle">
                <Link
                  key="view"
                  to={{
                    pathname: reverse(
                      `${routes.backOffice.companies.details.view}`,
                      {
                        companyId: record.id,
                      }
                    ),
                    search: location.search,
                  }}
                >
                  View
                </Link>
                <Link
                  key="edit"
                  to={{
                    pathname: reverse(
                      `${routes.backOffice.companies.details.edit}`,
                      {
                        companyId: record.id,
                      }
                    ),
                    search: location.search,
                  }}
                >
                  Edit
                </Link>
              </Space>
            ),
          },
        ]),
  ];

  return (
    <div style={{ height: "100%" }}>
      <PageHeader
        ghost={false}
        title="Company Accounts"
        extra={[
          userRole !== "support_team" && (
            <Button key="create" type="primary">
              <Link to={routes.backOffice.companies.new}>
                Create New Account
              </Link>
            </Button>
          ),
        ]}
      />
      <div
        style={{
          height: "calc(100vh - 137px)",
          overflowY: "auto",
          padding: 24,
        }}
      >
        <Table
          dataSource={companyData}
          columns={columns}
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: 10,
            total: totalCompanyCount,
            showSizeChanger: false,
            disabled: companyData.length === 0,
          }}
          onChange={handleTablePagination}
        />
      </div>
    </div>
  );
};

export default CompanyListing;